import {GridStack} from "gridstack";
import React, {Dispatch, SetStateAction, useEffect, useState} from "react";
import {CopyIcon, XIcon} from "lucide-react";
import {MdModeEdit} from "react-icons/md";
import {v4 as uuidv4} from "uuid";
import {GaugeWidget, Widget} from "../internalwidgets";
import {ButtonWithTooltip, dashboardJsonReplacer, dashboardJsonReviver, RuntimeVariable} from "../Dashboard";
import {cn} from "../../ui/lib/utils";
import {cloneWidget, removeWidget} from "../utils";
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter} from "../../ui/dialog";
import {GaugeChart} from "../../Charts/GaugeChart";
import {determineFullWidth, findParentGrid, getVariableResolvedValuesAtGroup} from "../utils/grid";
import {Button} from "../../ui/button";
import {GaugeSelectorPanel} from "./GaugeSelectorPanel";
import {useSearchParams} from "react-router-dom";

export function GaugeChartWidget(
    props: {
        editable: boolean,
        id?: string,
        widget: GaugeWidget,
        grids: Map<string, GridStack>,
        setGrids: Dispatch<SetStateAction<Map<string, GridStack>>>,
        widgets: Map<string, Widget>,
        setWidgets: Dispatch<SetStateAction<Map<string, Widget>>>,
        updateParentOfMount?: (id: string) => void
        ref?: React.MutableRefObject<any>
        setRootChildren: Dispatch<SetStateAction<Widget[]>>
        runtimeVariables: Map<string, RuntimeVariable[]>
    }
) {
    const [id] = React.useState<string>(props.id ?? "id-" + uuidv4())
    const [isFullWidth, setIsFullWidth] = useState<boolean>(false);
    const [widget, setWidget] = React.useState<GaugeWidget>(props.widget)
    const [resolvedVariables, setResolvedVariables] = React.useState<RuntimeVariable[]>(() => {
        let variableResolvedValuesAtGroup1 = getVariableResolvedValuesAtGroup(id, props.grids, props.runtimeVariables);
        return variableResolvedValuesAtGroup1
    })
    const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = React.useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const [environments, setEnvironments] = useState<string[]>([]);
    const [label, setLabel] = useState<string>(widget.label ?? "");

    // When we update the variables, we need to calculate new resolved values
    useEffect(() => {
        let variableResolvedValuesAtGroup = getVariableResolvedValuesAtGroup(id, props.grids, props.runtimeVariables);
        setResolvedVariables(variableResolvedValuesAtGroup)
    }, [props.runtimeVariables, props.grids])

    // Handle environment from search parameters
    useEffect(() => {
        let environment = searchParams.get("environment");
        if (environment !== null && environment !== "") {
            setEnvironments([environment]);
        } else {
            setEnvironments([]);
        }
    }, [searchParams]);

    useEffect(() => {
        const {element, grid} = findParentGrid(id, props.grids);
        const fullWidth = determineFullWidth(element, grid, props.widget.position ?? {});
        setIsFullWidth(fullWidth);
    }, [id, props.grids, props.widget.position, props]);

    useEffect(() => {
        props.setWidgets((prev) => {
            let newWidgets = new Map<string, Widget>(prev)
            newWidgets.set(id, props.widget)
            return newWidgets
        })

        if (props.updateParentOfMount !== undefined) {
            props.updateParentOfMount(id)
        }
    }, []);


    let leftMargin = "pl-1"
    if (props.widget.position?.x === 0) {
        leftMargin = "pl-2"
    }
    let rightMargin = "pr-1"
    if (isFullWidth) {
        rightMargin = "pr-2"
    }
    let topMargin = "pt-1"
    if (props.widget.position?.y === 0) {
        topMargin = "pt-2"
    }
    let bottomMargin = "pb-1"

    const handleClose = () => {
        setShowConfirmDialog(true);
    };

    const handleConfirmClose = () => {
        setShowConfirmDialog(false);
        setIsDialogOpen(false);
    };

    const handleCancelClose = () => {
        setShowConfirmDialog(false);
    };

    return (
        <div id={id} className={cn(id, "flex flex-col grow")}>
            <div className={cn("flex grow max-w-full max-h-full", leftMargin, topMargin, rightMargin, bottomMargin)}>
                <div className={cn("flex flex-col grow shrink max-w-full w-full", true ? "border rounded bg-backgroundmedium" : "")}>
                    { (
                        <div className={cn("flex-none flex draggablehandle justify-between max-w-full bg-backgroundmedium border-b pl-1 h-8 overflow-y-hidden")}>
                            <div className={"text-textmedium flex-col text-center items-center justify-center mt-[4px] truncate"}>
                                {label}
                            </div>
                            <div className={"flex gap-0"}>
                                {props.editable && (
                                    <>
                                        <div className={"flex flex-col justify-center"}>
                                            <ButtonWithTooltip
                                                icon={CopyIcon}
                                                tooltipText="Duplicate widget"
                                                onClick={() => {
                                                    cloneWidget(widget, props.setRootChildren)
                                                }}
                                            />
                                        </div>
                                        <div className={"flex flex-col items-center justify-center"}>
                                            <ButtonWithTooltip
                                                icon={MdModeEdit}
                                                tooltipText="Edit widget"
                                                onClick={() => setIsDialogOpen(true)}
                                            />
                                        </div>
                                        <Dialog modal={false} open={isDialogOpen}>
                                            <DialogContent
                                                onEscapeKeyDown={handleClose}
                                                onInteractOutside={handleClose}
                                                className={"w-screen h-screen m-0 p-0 bg-black/80 flex items-center justify-center border-none"}
                                            >
                                                <div className="w-[95vw] h-[95vh] flex">
                                                    <GaugeSelectorPanel
                                                        widget={JSON.parse(JSON.stringify(widget, dashboardJsonReplacer), dashboardJsonReviver) as GaugeWidget}
                                                        setIsDialogOpen={setIsDialogOpen}
                                                        runtimeVariables={resolvedVariables}
                                                        onSave={(updatedWidget) => {
                                                            props.setWidgets((prev) => {
                                                                let newWidgets = new Map<string, Widget>(prev)
                                                                newWidgets.set(id, updatedWidget)
                                                                return newWidgets
                                                            });
                                                            setWidget(updatedWidget);
                                                        }}
                                                        setLabel={setLabel}
                                                        label={label}
                                                    />
                                                </div>
                                            </DialogContent>
                                        </Dialog>

                                        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                                            <DialogContent className="sm:max-w-[525px] bg-backgroundmedium">
                                                <DialogHeader>
                                                    <DialogTitle>Close Gauge Editor?</DialogTitle>
                                                    <DialogDescription className={"text-textmedium"}>
                                                        Are you sure you want to close the gauge editor? Any changes will be discarded.
                                                    </DialogDescription>
                                                </DialogHeader>
                                                <DialogFooter>
                                                    <Button onClick={handleCancelClose} className={"text-textmedium border rounded border-primary bg-primarytransparent"}>
                                                        Back to Editor
                                                    </Button>
                                                    <Button onClick={handleConfirmClose} className={"text-textmedium border rounded border-amber-500 bg-amber-500/10"}>
                                                        Close Without Saving
                                                    </Button>
                                                </DialogFooter>
                                            </DialogContent>
                                        </Dialog>

                                        <div className={"flex flex-col items-center justify-center"}>
                                            <ButtonWithTooltip
                                                icon={XIcon}
                                                tooltipText="Delete widget"
                                                onClick={() => removeWidget(id, props.grids, props.setGrids, props.widgets, props.setWidgets)}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    <div className={"flex flex-col grow shrink w-full min-h-0"}>
                        <div className={cn("max-w-full w-full grow shrink flex flex-col max-h-full", props.editable ? "bg-backgroundmedium" : "")}>
                            <GaugeChart
                                // label={widget.label ?? ""}
                                min={widget.min ?? 0}
                                max={widget.max ?? 100}
                                precision={widget.precision ?? 2}
                                showAbsoluteValue={widget.showAbsoluteValue ?? false}
                                unitOverride={widget.unitOverride}
                                className="!p-0 w-full h-full"
                                runtimeVariables={resolvedVariables}
                                metricSpecifiers={widget.metricSpecifiers}
                                formulas={widget.formulas}
                                metoroQLQueries={widget.metoroQLQueries}
                                reduceOption={widget.reduceOption ?? "lastNotNull"}
                                environments={environments}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
